import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import css from "./index.module.css";
import Content from "@components/adminNavigation";
import * as Types from "../../../types";
import axios from "axios";
import { statusText } from "../../account/order.info";
import SEO from "@components/seo";
import Tabs from "@components/tabs";
import { toast } from "sonner";

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Types.Order[]>();
  const [sort, setSort] = useState<number>(0);
  const [orderSearch, setOrderSearch] = useState<string>("");
  const [organizationFilter, setOrganizationFilter] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);

  const [sortConfig, setSortConfig] = useState<{
    key: "createdAt" | "status";
    direction: "asc" | "desc";
  }>({ key: "createdAt", direction: "desc" });

  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] = useState<number | string>("");

  function convertToDate(isoDate: any) {
    const date = new Date(isoDate);
    return new Intl.DateTimeFormat("ru-Ru", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    }).format(date);
  }

  const fetchOrders = () => {
    axios
      .get(`${import.meta.env.VITE_API_ADM_URL}/orders`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        setOrders(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleSort = (key: "createdAt" | "status") => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleSelectRow = (id: string) => {
    if (selectedOrderIds.includes(id)) {
      setSelectedOrderIds(selectedOrderIds.filter((sid) => sid !== id));
    } else {
      setSelectedOrderIds([...selectedOrderIds, id]);
    }
  };

  const handleBulkUpdate = async () => {
    if (bulkStatus === "" || selectedOrderIds.length === 0) return;

    try {
      await Promise.all(
        selectedOrderIds.map((id) => {
          const formData = new FormData();
          formData.set("status", bulkStatus.toString());
          return axios.post(
            `${import.meta.env.VITE_API_ADM_URL}/order/${id}`,
            formData,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              },
            }
          );
        })
      );
      toast.success("Статусы обновлены");
      setSelectedOrderIds([]);
      setBulkStatus("");
      fetchOrders();
    } catch (error) {
      console.error(error);
      toast.error("Ошибка обновления");
    }
  };

  const filterOrders = (orders: Types.Order[]) => {
    const bySort = (() => {
      if (sort === 0)
        return orders.filter(
          (order) => order.status !== 5 && order.status !== 4
        );
      if (sort === 1)
        return orders.filter(
          (order) => order.status === 5 || order.status === 4
        );
      if (sort === 2) return orders.filter((order) => order.status === 2);
      if (sort === 3) return orders.filter((order) => order.status === 3);
      return orders;
    })();

    const byFilters = bySort.filter((order) => {
      const orderDate = new Date(order.createdAt);

      if (dateFrom && orderDate < new Date(`${dateFrom}T00:00:00`)) {
        return false;
      }

      if (dateTo && orderDate > new Date(`${dateTo}T23:59:59`)) {
        return false;
      }

      if (
        organizationFilter &&
        order.organization?.orgId !== organizationFilter
      ) {
        return false;
      }

      return true;
    });

    const q = orderSearch.trim().toLowerCase();
    let result = byFilters;

    if (q) {
      result = byFilters.filter((order) => {
        const idText = String(order.id || "").toLowerCase();
        const shortIdText = String(order.shortID || "").toLowerCase();
        const dateText = convertToDate(order.createdAt).toLowerCase();
        const orgNameText = (order.organization?.name || "").toLowerCase();
        const orgInnText = (order.organization?.inn || "").toLowerCase();
        const orgOgrnText = (order.organization?.ogrn || "").toLowerCase();
        const userNameText = (order.user?.username || "").toLowerCase();
        const userPhoneText = (order.user?.phone || "").toLowerCase();
        const addressText = (order.address || "").toLowerCase();
        return (
          idText.includes(q) ||
          shortIdText.includes(q) ||
          dateText.includes(q) ||
          orgNameText.includes(q) ||
          orgInnText.includes(q) ||
          orgOgrnText.includes(q) ||
          userNameText.includes(q) ||
          userPhoneText.includes(q) ||
          addressText.includes(q)
        );
      });
    }

    return result.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const organizationOptions =
    orders
      ?.reduce<Types.Organization[]>((acc, order) => {
        if (order.organization) {
          const exists = acc.some(
            (org) => org.orgId === order.organization?.orgId
          );
          if (!exists) {
            acc.push(order.organization);
          }
        }
        return acc;
      }, [])
      .sort((a, b) => a.name.localeCompare(b.name, "ru")) || [];

  const filteredOrders = filterOrders(orders || []);
  
  const allSelected =
    filteredOrders.length > 0 &&
    filteredOrders.every((o) => selectedOrderIds.includes(o.id));

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedOrderIds([]);
    } else {
      const visibleIds = filteredOrders.map((o) => o.id);
      setSelectedOrderIds(Array.from(new Set([...selectedOrderIds, ...visibleIds])));
    }
  };

  return (
    <Content>
      <SEO
        title={"Заказы"}
        description={"Админ-панель"}
        ogUrl={`https://рыбныимир.рф/`}
      />

      <h1 className={css.head}>Список заказов</h1>

      <div className={css.filter}>
        <input
          type="text"
          placeholder="Поиск (ID, организация, клиент, дата, адрес)"
          value={orderSearch}
          onChange={(e) => setOrderSearch(e.target.value)}
        />
        <button
          type="button"
          className={css.filterToggle}
          onClick={() => setIsFiltersOpen((prev) => !prev)}
        >
          Фильтры
          <i
            className={
              isFiltersOpen ? "fi fi-rr-angle-small-up" : "fi fi-rr-angle-small-down"
            }
          />
        </button>
      </div>

      {isFiltersOpen && (
        <div className={css.filterDropdown}>
          <div className={css.filterRow}>
            <label>Организация</label>
            <select
              value={organizationFilter}
              onChange={(e) => setOrganizationFilter(e.target.value)}
            >
              <option value="">Все организации</option>
              {organizationOptions.map((org) => (
                <option key={org.orgId} value={org.orgId}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
          <div className={css.filterRow}>
            <label>Дата с</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className={css.filterRow}>
            <label>Дата по</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <button
            type="button"
            className={css.filterReset}
            onClick={() => {
              setOrganizationFilter("");
              setDateFrom("");
              setDateTo("");
            }}
          >
            Сбросить фильтры
          </button>
        </div>
      )}

      <Tabs
        sections={[
          {
            id: 0,
            name: "все",
          },
          {
            id: 1,
            name: "отмененные/завершенные",
          },
          {
            id: 2,
            name: "в сборке",
          },
          {
            id: 3,
            name: "в пути",
          },
        ]}
        active={sort}
        setActive={setSort}
      />

      {selectedOrderIds.length > 0 && (
        <div className={css.bulkActions}>
          <div style={{ marginRight: "auto" }}>
            Выбрано: <b>{selectedOrderIds.length}</b>
          </div>
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
            style={{ padding: "5px", borderRadius: "5px" }}
          >
            <option value="">Выберите статус</option>
            {Object.entries(statusText).map(([key, val]) => (
              <option key={key} value={key}>
                {val}
              </option>
            ))}
          </select>
          <button className={css.bulkBtn} onClick={handleBulkUpdate}>
            Применить
          </button>
        </div>
      )}

      <div className={css.table}>
        <div className={css.tableHeader}>
          <div
            className={css.checkbox}
            onClick={handleSelectAll}
          >
            <input
              type="checkbox"
              checked={allSelected}
              readOnly
              style={{ pointerEvents: "none" }}
            />
          </div>
          <a>№</a>
          <a>Организация</a>
          <div
            className={css.sortable}
            onClick={() => handleSort("createdAt")}
            style={{ width: "100%", fontWeight: 600 }}
          >
            Дата {sortConfig.key === "createdAt" && (sortConfig.direction === "asc" ? "↑" : "↓")}
          </div>
          <div
            className={css.sortable}
            onClick={() => handleSort("status")}
            style={{ width: "100%", fontWeight: 600 }}
          >
            Статус {sortConfig.key === "status" && (sortConfig.direction === "asc" ? "↑" : "↓")}
          </div>
        </div>

        <div className={css.tableBody}>
          {filteredOrders?.map((item) => (
            <div
              key={item.id}
              className={css.tableDiv}
              onClick={() => navigate(`/manage/order/${item.id}`)}
            >
              <div
                className={css.checkbox}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectRow(item.id);
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedOrderIds.includes(item.id)}
                  readOnly
                  style={{ pointerEvents: "none" }}
                />
              </div>
              <a>{item.shortID}</a>
              <a>{item.organization?.name || "Не указано"}</a>
              <a>{convertToDate(item.createdAt)}</a>
              <a>{statusText[item.status]}</a>
            </div>
          ))}

          {filteredOrders?.length === 0 && (
            <a className={css.empty}>Нет заказов</a>
          )}
        </div>
      </div>
    </Content>
  );
}
